import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ImageData, FontType, defaultFont, FrameType, defaultFrame } from '../redux/cardAttributeModelIndex';
import generateId from './generateId';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

type ShapeBase = {
  cardTemplateId: string;
  name: string;
  x: number;
  y: number;
  z: number;
  width: number;
  height: number;
  rotation: number;
  frame: FrameType;
  background: { display: boolean; color: string; };
  goodsId?: string;
  version?: number;
}
export type CardTemplateTextShape = ShapeBase & {
  kind: 'text';
  text: string;
  font: FontType;
};
export type CardTemplateCircleShape = ShapeBase & {
  kind: 'circle';
  stroke: {display: boolean, color: string, opacity: number, width: number};
  fill: {display: boolean, color: string, opacity: number};
};
export type CardTemplateRectangleShape = ShapeBase & {
  kind: 'rectangle';
  stroke: {display: boolean, color: string, opacity: number, width: number};
  fill: {display: boolean, color: string, opacity: number};
};
export type CardTemplateBalloonShape = ShapeBase & {
  kind: 'balloon';
  stroke: {display: boolean, color: string, opacity: number, width: number};
  fill: {display: boolean, color: string, opacity: number};
};
export type CardTemplateImageShape = ShapeBase & {
  kind: 'image';
  image: ImageData;
};
export type CardTemplateShapeDb = CardTemplateTextShape | CardTemplateRectangleShape | CardTemplateCircleShape | CardTemplateBalloonShape | CardTemplateImageShape;

const initialBase: ShapeBase = {
  cardTemplateId: '',
  name: '',
  x: 0,
  y: 0,
  z: 0,
  width: 0,
  height: 0,
  rotation: 0,
  frame: defaultFrame,
  background: { display: false, color: '#ffffff' },
  goodsId: undefined,
  version: undefined
}
type CardTemplateShapeInitialType = CardTemplateShapeDb[]
const initial: CardTemplateShapeInitialType = [
  {
    ...initialBase,
    kind: 'text',
    text: '',
    font: defaultFont,
  },
  {
    ...initialBase,
    kind: 'rectangle',
    stroke: {display: false, color: '', opacity: 0, width: 0},
    fill: {display: false, color: '', opacity: 0}
  },
  {
    ...initialBase,
    kind: 'circle',
    stroke: {display: false, color: '', opacity: 0, width: 0},
    fill: {display: false, color: '', opacity: 0}
  },
  {
    ...initialBase,
    kind: 'balloon',
    stroke: {display: false, color: '', opacity: 0, width: 0},
    fill: {display: false, color: '', opacity: 0}
  },
  {
    ...initialBase,
    kind: 'image',
    image: {
      imageId: '',
      originWidth: 0,
      originHeight: 0,
      trimXFrom: 0,
      trimYFrom: 0,
      trimXTo: 0,
      trimYTo: 0
    }
  },
]

export type CardTemplateShapeDbHashType = {
  [key in string]: CardTemplateShapeDb;
}

export type CardTemplateShapeAddPayload = {
  id?: string;
} & CardTemplateShapeDb;
export type CardTemplateShapeUpdatePayload = {
  id: string;
} & {[P in keyof CardTemplateShapeDb]?: CardTemplateShapeDb[P]};

export type CardTemplateShapeDbState = {
  cardTemplateShapes: CardTemplateShapeDbHashType;
}

const isCardTemplateShapeDb = (obj: any): obj is CardTemplateShapeDb => {
  return 'kind' in obj && 'cardTemplateId' in obj;
}
export const isCardTemplateImageShape = (obj: any): obj is CardTemplateImageShape => {
  return 'kind' in obj && obj.kind === 'image' && 'cardTemplateId' in obj;
}
const isSameType = <T, _>(a: T, b: any): b is T => {
  return isCardTemplateShapeDb(a) && a.kind === b?.kind;
}

export const generateCardTemplateShapeId = (): string => generateId('cts');
let initialState: CardTemplateShapeDbState = { cardTemplateShapes: {} };
const key = 'cardTemplateShapes';

const cardTemplateShapeDbSlice = createSlice({
  name: 'cardTemplateShapeDb',
  initialState,
  reducers: {
    initializeCardTemplateShapes(state: CardTemplateShapeDbState) {
      state.cardTemplateShapes = {};
    },
    addCardTemplateShape(state: CardTemplateShapeDbState, action: PayloadAction<CardTemplateShapeAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateCardTemplateShapeId();
      state.cardTemplateShapes[id] = obj;
      saveData(key, id, obj);
    },
    updateCardTemplateShape(state: CardTemplateShapeDbState, action: PayloadAction<CardTemplateShapeUpdatePayload>) {
      const { id, ...obj } = action.payload;
      if (state.cardTemplateShapes[id] === undefined) {
        console.log(`*** CardTemplateShape not found: ${id}`);
      } else if (isSameType(state.cardTemplateShapes[id], obj)) {
        const newData = { ...state.cardTemplateShapes[id], ...obj };
        state.cardTemplateShapes[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteCardTemplateShape(state: CardTemplateShapeDbState, action: PayloadAction<CardTemplateShapeUpdatePayload>) {
      const id = action.payload.id;
      delete state.cardTemplateShapes[id];
      deleteData(key, id);
    },
    saveCardTemplateShapes(state: CardTemplateShapeDbState) {
      saveAllData(key, state.cardTemplateShapes);
    },
    signUpCardTemplateShapes(state: CardTemplateShapeDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.cardTemplateShapes, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: CardTemplateShapeDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.cardTemplateShapes = updatedValues;
      if (changed) saveAllData(key, state.cardTemplateShapes);
    })
  }
})

export const { initializeCardTemplateShapes, addCardTemplateShape, updateCardTemplateShape, deleteCardTemplateShape, saveCardTemplateShapes, signUpCardTemplateShapes } = cardTemplateShapeDbSlice.actions;
export default cardTemplateShapeDbSlice.reducer;
